import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";

const PAIRS = [
  {
    key: "room",
    label: "Deep Cleaning",
    title: "Living Room Transformation",
    before: "https://images.pexels.com/photos/8148857/pexels-photo-8148857.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    after: "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxicmlnaHQlMjBjbGVhbiUyMG1vZGVybiUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzkyNzQzMjM0fDA&ixlib=rb-4.1.0&q=85",
    desc: "Dull cluttered space restored to a glowing, dust-free, perfectly styled living room.",
  },
  {
    key: "lawn",
    label: "Lawn & Garden",
    title: "Tropical Garden & Lawn Restoration",
    before: "https://images.unsplash.com/photo-1706611893334-c50aaca22dbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxvdmVyZ3Jvd24lMjB3aWxkJTIwZ2FyZGVuJTIwZ3Jhc3MlMjB3ZWVkc3xlbnwwfHx8fDE3OTI3NDMyMzR8MA&ixlib=rb-4.1.0&q=85",
    after: "https://images.unsplash.com/photo-1625766924110-efbb2db1d1d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxncmVlbiUyMGdyYXNzJTIwZmllbGQlMjB3aXRoJTIwdHJlZXN8ZW58MHx8fHwxNzkyNzQzMjM0fDA&ixlib=rb-4.1.0&q=85",
    desc: "Overgrown patchy weeds transformed into a sharply manicured, lush green coastal lawn.",
  },
  {
    key: "paint",
    label: "Painting",
    title: "Wall Painting & Surface Refinishing",
    before: "https://images.pexels.com/photos/10647626/pexels-photo-10647626.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    after: "https://images.unsplash.com/photo-1625585598750-3535fe40efb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxmcmVzaGx5JTIwcGFpbnRlZCUyMGNsZWFuJTIwaW50ZXJpb3IlMjB3YWxsJTIwaG9tZXxlbnwwfHx8fDE3OTI3NDMyMzh8MA&ixlib=rb-4.1.0&q=85",
    desc: "Worn peeling plaster replaced with a crisp, weather-resistant fresh paint finish.",
  },
];

function CompareSlider({ pair }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, p)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-[4/3] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-3xl shadow-[0_35px_70px_-30px_rgba(28,59,43,0.5)] sm:aspect-[16/9]"
      onPointerDown={(e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img src={pair.after} alt={`${pair.title} — after KeDe-Ceaning`} draggable="false" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <img
        src={pair.before}
        alt={`${pair.title} — before KeDe-Ceaning`}
        draggable="false"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      <span className="absolute left-4 top-4 rounded-full bg-black/55 px-4 py-1.5 font-jbm text-[10px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
        Before
      </span>
      <span className="absolute right-4 top-4 rounded-full bg-[#1C3B2B]/85 px-4 py-1.5 font-jbm text-[10px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
        After
      </span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.4)]" />
        <div className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-[#C85A32] text-white shadow-xl">
          <ChevronsLeftRight size={20} />
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [active, setActive] = useState("room");
  const pair = PAIRS.find((p) => p.key === active);

  return (
    <section id="results" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-12 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end"
      >
        <div className="max-w-xl">
          <p className="font-jbm text-xs font-semibold uppercase tracking-[0.3em] text-[#C85A32]">Proof, not promises</p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#1C3B2B] sm:text-4xl lg:text-5xl">
            The KeDe-Ceaning difference
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#525B53] sm:text-lg">
            Drag the handle and see what one visit does to a room, a lawn, or a tired wall.
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {PAIRS.map((p) => (
            <button
              key={p.key}
              data-testid={`before-after-tab-${p.key}`}
              onClick={() => setActive(p.key)}
              className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
                active === p.key
                  ? "border-[#1C3B2B] bg-[#1C3B2B] text-[#FBF9F5]"
                  : "border-[#E4DDD3] bg-white text-[#525B53] hover:border-[#1C3B2B]/40"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div
          key={pair.key}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <CompareSlider pair={pair} />
          <div className="mt-6 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
            <h3 className="font-display text-xl font-bold text-[#1C3B2B] sm:text-2xl">{pair.title}</h3>
            <p className="max-w-md text-sm text-[#525B53]">{pair.desc}</p>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
